import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LiveChat from '../components/LiveChat';
import QAChatbot from '../components/QAChatBot';
import GetQuoteButton from '../components/GetQuoteButton';
import ScrollSocialIcons from '../components/ScrollSocialIcons';
import BackToTopButton from '../components/BackToTopButton';
import '../App.css';

const PrivacyPolicy = () => {
    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6 }
    };

    return (
        <div className="legal-page-wrapper">
            <Navbar />

            {/* Hero Section */}
            <section className="legal-hero">
                <div className="legal-hero-overlay"></div>
                <motion.div
                    className="legal-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="legal-hero-icon">🔒</div>
                    <h1>Privacy Policy</h1>
                    <p>Your privacy matters to us. Learn how CIAP Ltd protects your personal data.</p>
                    <div className="legal-last-updated">
                        Last Updated: May 25, 2026
                    </div>
                </motion.div>
            </section>

            {/* Content */}
            <section className="legal-content-section">
                <div className="legal-container">

                    <motion.div className="legal-intro" {...fadeInUp}>
                        <p>
                            At CIAP (Pvt) Ltd ("CIAP", "we", "our", or "us"), we are committed to protecting and respecting your privacy.
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our
                            website, use our services, or engage with us in any capacity related to our marine engineering, ship repairs,
                            and industrial solutions.
                        </p>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">01</div>
                        <h2>Information We Collect</h2>
                        <div className="legal-card">
                            <h3>Personal Information</h3>
                            <ul>
                                <li>Full name, job title, and company affiliation</li>
                                <li>Email address and phone number</li>
                                <li>Postal address and country of residence</li>
                                <li>Information provided via contact forms, quotation requests, or job applications</li>
                                <li>Supplier registration details including certifications and business references</li>
                            </ul>
                        </div>
                        <div className="legal-card">
                            <h3>Automatically Collected Information</h3>
                            <ul>
                                <li>IP address and browser type</li>
                                <li>Device information and operating system</li>
                                <li>Pages visited, time spent, and navigation patterns</li>
                                <li>Referring website URLs</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">02</div>
                        <h2>How We Use Your Information</h2>
                        <div className="legal-grid">
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">⚓</div>
                                <h4>Service Delivery</h4>
                                <p>To provide our marine engineering, docking, ship repair, and industrial services to clients and partners.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">📧</div>
                                <h4>Communication</h4>
                                <p>To respond to inquiries, process quotation requests, and provide updates about our services and operations.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">📊</div>
                                <h4>Analytics</h4>
                                <p>To analyze website usage, improve user experience, and optimize our digital presence.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🛡️</div>
                                <h4>Compliance</h4>
                                <p>To comply with legal obligations, maritime regulations, and industry standards applicable to our operations.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">👥</div>
                                <h4>Recruitment</h4>
                                <p>To process job applications submitted through our careers portal and manage potential candidate relationships.</p>
                            </div>
                            <div className="legal-grid-item">
                                <div className="legal-grid-icon">🤝</div>
                                <h4>Partnerships</h4>
                                <p>To evaluate and onboard suppliers, subcontractors, and business partners through our registration processes.</p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">03</div>
                        <h2>Cookies & Tracking</h2>
                        <p>We use cookies and similar technologies to enhance your browsing experience. These include:</p>
                        <div className="legal-table-wrapper">
                            <table className="legal-table">
                                <thead>
                                    <tr>
                                        <th>Cookie Type</th>
                                        <th>Purpose</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><span className="legal-badge essential">Essential</span></td>
                                        <td>Required for website functionality, security, and navigation</td>
                                        <td>Session</td>
                                    </tr>
                                    <tr>
                                        <td><span className="legal-badge analytics">Analytics</span></td>
                                        <td>Help us understand how visitors use our site</td>
                                        <td>Up to 2 years</td>
                                    </tr>
                                    <tr>
                                        <td><span className="legal-badge functional">Functional</span></td>
                                        <td>Remember your preferences and settings</td>
                                        <td>Up to 1 year</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p className="legal-note">You can manage your cookie preferences through your browser settings at any time.</p>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">04</div>
                        <h2>Data Security</h2>
                        <p>
                            We implement industry-standard security measures to protect your personal information, including:
                        </p>
                        <div className="legal-card">
                            <ul>
                                <li>Encrypted data transmission (SSL/TLS) across all web interactions</li>
                                <li>Secure server infrastructure with access controls and monitoring</li>
                                <li>Regular security assessments and vulnerability testing</li>
                                <li>Restricted access to personal data on a need-to-know basis</li>
                                <li>Staff training on data protection and information security</li>
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">05</div>
                        <h2>Third-Party Sharing</h2>
                        <p>We do not sell your personal information. We may share your data with:</p>
                        <div className="legal-card">
                            <ul>
                                <li><strong>Service Providers:</strong> Trusted partners who assist in website hosting, analytics, email delivery, and IT support</li>
                                <li><strong>Regulatory Bodies:</strong> Maritime authorities, port authorities, and government agencies when legally required</li>
                                <li><strong>Business Partners:</strong> Shipyards, classification societies, and approved subcontractors necessary for service delivery</li>
                                <li><strong>Legal Requirements:</strong> When disclosure is necessary to comply with applicable laws, court orders, or legal processes</li>
                            </ul>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">06</div>
                        <h2>Your Rights</h2>
                        <p>You have the following rights regarding your personal data:</p>
                        <div className="legal-grid">
                            <div className="legal-grid-item compact">
                                <h4>Access</h4>
                                <p>Request a copy of the personal data we hold about you</p>
                            </div>
                            <div className="legal-grid-item compact">
                                <h4>Correction</h4>
                                <p>Request correction of inaccurate or incomplete information</p>
                            </div>
                            <div className="legal-grid-item compact">
                                <h4>Deletion</h4>
                                <p>Request deletion of your personal data where legally applicable</p>
                            </div>
                            <div className="legal-grid-item compact">
                                <h4>Objection</h4>
                                <p>Object to certain types of processing of your personal data</p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">07</div>
                        <h2>Data Retention</h2>
                        <div className="legal-card">
                            <p>
                                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy,
                                or as required by applicable laws and regulations. When your data is no longer needed, we will securely
                                delete or anonymize it in accordance with our data retention procedures.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div className="legal-section" {...fadeInUp}>
                        <div className="legal-section-number">08</div>
                        <h2>Contact Us</h2>
                        <div className="legal-contact-card">
                            <p>If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us:</p>
                            <div className="legal-contact-details">
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Email</span>
                                    <a href="mailto:info@ciap.lk">info@ciap.lk</a>
                                </div>
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Address</span>
                                    <span>CIAP (Pvt) Ltd, Sri Lanka</span>
                                </div>
                                <div className="legal-contact-item">
                                    <span className="legal-contact-label">Website</span>
                                    <a href="https://www.ciap.lk">www.ciap.lk</a>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </section>

            <LiveChat />
            <QAChatbot />
            <GetQuoteButton />
            <ScrollSocialIcons />
            <BackToTopButton />
            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
